import { v1 as uuidv1 } from "uuid";

import { Animation, GetAnimationDataQuery } from "@/gql/generated/graphql";
import { getBlankImage } from "@/lib/canvas/blank-image";
import { FrameImage } from "@/lib/canvas/frame-image";
import { loadFramesService } from "@/lib/services/animation";
import { RootState } from "@/lib/state/store";

import { AnimationState } from "./state/reducers/animation-default-state";
import { unserialiseSequence } from "./editor-helpers";

type OnLoadProgressCBArgs = {
  resolved: number;
  total: number;
};

export const loadServerImages = async (
  animationData: NonNullable<GetAnimationDataQuery["animation"]>,
  onLoadProgress: ({ total, resolved }: OnLoadProgressCBArgs) => void,
): Promise<Omit<AnimationState, "saved" | "savedLocally" | "folderId">> => {
  const { sequence, imagesToLoad } = unserialiseSequence({
    sequence: animationData.sequence,
  });

  let errorOccurred = false;

  const onFrameCompleted = () => {
    let resolved = 0;
    const total = imagesToLoad.length;
    return () => {
      if (errorOccurred) return;
      resolved++;
      onLoadProgress({ resolved, total });
    };
  };

  onLoadProgress({ resolved: 0, total: animationData.frames });

  const layers =
    animationData.layers && animationData.layers.length > 0
      ? animationData.layers
      : ["Background", "Foreground"];

  let resolvedImages: FrameImage[];
  try {
    const loaded = await loadFramesService({
      imagesToLoad,
      animationData,
      onFrameCompleted: onFrameCompleted(),
    });
    resolvedImages = loaded.images;
  } catch (e) {
    errorOccurred = true;
    throw e;
  }

  const resolvedFrameImages: Record<string, FrameImage> = {};
  const sequenceWithImages = sequence.map((frame) => {
    const { layers } = frame;
    return {
      ...frame,
      layers: layers.map((layerImageIndex) => {
        if (resolvedFrameImages[layerImageIndex]) {
          // Image has already been resolved
          return {
            id: uuidv1(),
            image: resolvedFrameImages[layerImageIndex],
          };
        } else {
          // Image referenced for the first time
          if (layerImageIndex !== "_blank") {
            resolvedFrameImages[layerImageIndex] =
              resolvedImages[parseInt(layerImageIndex, 10)];
            return {
              id: uuidv1(),
              image: resolvedFrameImages[layerImageIndex],
            };
          } else {
            return {
              id: uuidv1(),
              image: getBlankImage({
                width: animationData.width,
                height: animationData.height,
              }),
            };
          }
        }
      }),
    };
  });

  return {
    ...animationData,
    layers,
    sequence: sequenceWithImages,
  };
};
